import { getProductsSettings } from '@sonifi/wb-products/services/products.service';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import {
  defaultProductsTabsMapped,
  handlePermissions,
} from './helper/navHelper';
import { defaultProductsTab } from './helper/tabs';
import { GROUP_MARKET_TYPES } from '@sonifi/wb-group/constants/wbGroupConstants';
import { NAV_KEYS } from '../constants/wbNavConstants';
import { visitPageGA } from '@sonifi/wb-core/services/ga.service';
import { useWbNavBlock } from './useWbNavBlock';
import { ROUTES_PATH } from '@sonifi/wb-core/routes/routesConstants';

export const useWbProductNav = ({ secondaryNav, tertiaryNav }) => {
  const selectedGroup = useSelector((state) => state.groups.selectedGroup);
  const revenueCenterSelected = useSelector(
    (state) => state.irdSettings.revenueCenterSelected,
  );
  const user = useSelector((state) => state.user.user);
  const [productTabs, setProductTabs] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { platform, slideId } = useParams();

  const wbProducts = useSelector((state) => state.products.products);

  const productsTabs = useMemo(
    () =>
      defaultProductsTabsMapped({ products: wbProducts, defaultProductsTab }),
    [wbProducts],
  );

  const [productsNavState, setProductsNavState] = useState({
    secondaryNavState: null,
    tertiaryNavState: null,
  });

  // Tracking values to avoid re-renders
  const refValues = useRef({
    group: selectedGroup,
    settings: [],
    revenueCenterSelected,
    productsNavState,
    previousProductsNavState: productsNavState,
  });
  refValues.current.group = selectedGroup;
  refValues.current.productsNavState = productsNavState;
  refValues.current.revenueCenterSelected = revenueCenterSelected;

  useWbNavBlock({
    onRestoreNav: () => {
      setProductsNavState(refValues.current.previousProductsNavState);
    },
  });

  const getPlatform = useCallback(
    (nav) => {
      if (platform) {
        return platform;
      }
      const currentPlatform =
        selectedGroup.market_type === GROUP_MARKET_TYPES.healthcare &&
        nav.key !== NAV_KEYS.hotelDirectory
          ? 'hctv'
          : 'tv';
      return currentPlatform;
    },
    [platform, selectedGroup],
  );

  const buildSecondaryNav = useCallback(
    (productsSettings) =>
      productsTabs.navChildren
        .map((pv) => {
          const foundProduct = productsSettings.find(
            (ps) => ps.product === pv.id,
          );

          return {
            ...pv,
            enabled: foundProduct?.enabled || false,
          };
        })
        .filter((nv) => handlePermissions({ tab: nv, user }) && nv.enabled),
    [user, productsTabs.navChildren],
  );

  const buildNavigation = useCallback(
    async (nv) => {
      try {
        const platform = getPlatform(nv);

        if (nv?.dispatchLoader) {
          await dispatch(
            nv.dispatchLoader({
              groupId: refValues.current.group.id,
            }),
          );
        }

        if (nv?.url) {
          const url = nv.buildUrl({
            groupId: refValues.current.group.id,
            platform,
            revenueCenterId: refValues.current.revenueCenterSelected?.id,
            slideId,
          });

          return { ...nv, url };
        } else {
          const navChildren = nv.navChildren
            .map((nvc) => {
              const url = nvc.buildUrl({
                groupId: refValues.current.group.id,
                platform,
                revenueCenterId: refValues.current.revenueCenterSelected?.id,
              });

              const foundProduct = refValues.current.settings.find(
                (ps) => ps.product === nvc.id,
              );
              const isVisible = nvc?.isVisible
                ? nvc.isVisible(foundProduct)
                : true;
              return {
                ...nvc,
                url,
                enabled: (foundProduct?.enabled && isVisible) || false,
              };
            })
            .filter((nv) => handlePermissions({ tab: nv, user }) && nv.enabled);

          return { ...nv, navChildren };
        }
      } catch (error) {
        console.error('Error building product secondary nav', error);
        return {
          ...nv,
          navChildren: [],
        };
      }
    },
    [dispatch, getPlatform, slideId, user],
  );

  const handleTertiaryNavClick = useCallback(
    async (nv) => {
      // Update previous values
      refValues.current.previousProductsNavState = productsNavState;
      // Update current values
      setProductsNavState((prev) => ({ ...prev, tertiaryNavState: nv }));
      const tertiaryNav = await buildNavigation(nv);
      dispatch(visitPageGA({ page: tertiaryNav.url, key: tertiaryNav.key }));
      navigate(tertiaryNav.url);
    },
    [dispatch, navigate, productsNavState, buildNavigation],
  );

  const handleSecondaryNavClick = useCallback(
    async (nv) => {
      // Update previous values
      refValues.current.previousProductsNavState = productsNavState;
      // Update current values
      setProductsNavState((prev) => ({ ...prev, secondaryNavState: nv }));
      const secondaryNav = await buildNavigation(nv);
      if (secondaryNav.url) {
        dispatch(
          visitPageGA({ page: secondaryNav.url, key: secondaryNav.key }),
        );
        navigate(secondaryNav.url);
      } else {
        handleTertiaryNavClick(nv.navChildren[0]);
      }
    },
    [
      dispatch,
      navigate,
      productsNavState,
      buildNavigation,
      handleTertiaryNavClick,
    ],
  );

  const handleGroupChange = useCallback(
    async ({ groupSel, goHome = false }) => {
      // Get products settings
      const productsSettings = await dispatch(getProductsSettings(groupSel.id));
      refValues.current.settings = productsSettings.settings;
      // Build product navigation
      const newNv = buildSecondaryNav(productsSettings.settings);
      const pTabs = { ...productsTabs, navChildren: newNv };
      setProductTabs(pTabs);

      if (goHome && location.pathname !== '/') {
        const currentSecondaryKey =
          refValues.current.productsNavState.secondaryNavState?.key;
        const matchingTab = currentSecondaryKey
          ? newNv.find((nv) => nv.key === currentSecondaryKey)
          : null;

        if (matchingTab) {
          // Point to the new group so buildNavigation constructs URLs for the new site
          refValues.current.group = groupSel;
          const nav = await buildNavigation(matchingTab);

          let destUrl;
          if (nav.url) {
            destUrl = nav.url;
          } else {
            // Product has tertiary nav (e.g. IRD, GuestRequest, Branding).
            // Preserve the current tertiary section if it exists on the new site.
            const currentTertiaryKey =
              refValues.current.productsNavState.tertiaryNavState?.key;
            const matchingTertiary = currentTertiaryKey
              ? nav.navChildren?.find((nv) => nv.key === currentTertiaryKey)
              : null;
            destUrl =
              matchingTertiary?.url ?? nav.navChildren?.[0]?.url ?? null;
          }

          navigate(
            destUrl ?? `${ROUTES_PATH.PRODUCTS.PATH}/${groupSel.id}`,
          );
        } else {
          navigate(`${ROUTES_PATH.PRODUCTS.PATH}/${groupSel.id}`);
        }
      }
      return pTabs;
    },
    [dispatch, productsTabs, buildSecondaryNav, navigate, buildNavigation],
  );

  const onChangeRevenueCenter = useCallback(
    (revenueCenter) => {
      const nextUrl = productsNavState.tertiaryNavState.buildUrl({
        groupId: refValues.current.group.id,
        revenueCenterId: revenueCenter.id,
        platform,
      });
      navigate(nextUrl);
    },
    [navigate, platform, productsNavState.tertiaryNavState],
  );

  useEffect(() => {
    // Initialize product tabs if group is selected but tabs haven't been loaded yet - i.e when the user refresh the page
    if (refValues.current.group?.id && !productTabs) {
      handleGroupChange({ groupSel: refValues.current.group }).then((pTabs) => {
        const selectedSecondaryNav = pTabs?.navChildren?.find(
          (nv) => nv.key === secondaryNav,
        );
        setProductsNavState((prev) => ({
          ...prev,
          secondaryNavState: selectedSecondaryNav,
        }));
        const selectedTertiaryNav = selectedSecondaryNav?.navChildren?.find(
          (nv) => nv.key === tertiaryNav,
        );
        setProductsNavState((prev) => ({
          ...prev,
          tertiaryNavState: selectedTertiaryNav,
        }));
      });
      return;
    }

    // Update nav state when URL parameters change (secondaryNav or tertiaryNav) - i.e when the user back and forth the browser history
    if (
      refValues.current.productsNavState.secondaryNavState?.key !==
        secondaryNav ||
      refValues.current.productsNavState.tertiaryNavState?.key !== tertiaryNav
    ) {
      const selectedSecondaryNav = productTabs?.navChildren?.find(
        (nv) => nv.key === secondaryNav,
      );

      setProductsNavState((prev) => ({
        ...prev,
        secondaryNavState: selectedSecondaryNav,
      }));

      const selectedTertiaryNav = selectedSecondaryNav?.navChildren?.find(
        (nv) => nv.key === tertiaryNav,
      );
      setProductsNavState((prev) => ({
        ...prev,
        tertiaryNavState: selectedTertiaryNav,
      }));
    }
  }, [handleGroupChange, productTabs, secondaryNav, tertiaryNav]);

  return {
    productsNavState,
    productTabs,
    group: selectedGroup,
    handleGroupChange,
    onChangeRevenueCenter,
    handleTertiaryNavClick,
    handleSecondaryNavClick,
  };
};
